import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ImportExecutionStatus, LOGICAL_FILE_TYPES } from '@today/types'
import type { ImportExecutionListViewProps } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IMPORTS_NAMESPACE } from '@/i18n'
import { importMonitoringService } from '@/services/import-monitoring.service'
import { enumOptions, listParams, toRowView } from './imports-ui'

export const IMPORTS_KEY = ['imports', 'executions'] as const

/**
 * The executions list (TO-399 REQ-009): 20 per page, newest first (server
 * order), status and logical-file filters that reset to page 1.
 */
export function useImportExecutionList(onOpen: (id: string) => void): ImportExecutionListViewProps {
	const { t, i18n } = useTranslation(IMPORTS_NAMESPACE)
	const [status, setStatus] = useState('')
	const [logicalFile, setLogicalFile] = useState('')
	const [page, setPage] = useState(1)
	const params = listParams(page, { status, logicalFile })
	const list = useQuery({
		queryKey: [...IMPORTS_KEY, params],
		queryFn: () => importMonitoringService.list(params),
		placeholderData: keepPreviousData,
	})
	return {
		t,
		rows: (list.data?.data ?? []).map((item) => toRowView(item, t, i18n.language)),
		statusFilter: status,
		statusOptions: enumOptions(t, 'status', Object.values(ImportExecutionStatus)),
		logicalFileFilter: logicalFile,
		logicalFileOptions: enumOptions(t, 'logicalFile', LOGICAL_FILE_TYPES),
		page,
		pageCount: list.data?.totalPages ?? 0,
		error: list.isError,
		onStatusChange: (next) => {
			setStatus(next)
			setPage(1)
		},
		onLogicalFileChange: (next) => {
			setLogicalFile(next)
			setPage(1)
		},
		onPageChange: setPage,
		onOpen,
		onRetry: () => void list.refetch(),
	}
}
